/**
 * Route Announcer
 *
 * On a route change: preserves focus intentionally claimed by the destination,
 * otherwise moves it to the destination fragment, visible page `h1`, or page
 * landmark, and announces "{Page} loaded" in a polite live region. Without this,
 * keyboard focus stays on the link that was activated and a screen reader says
 * nothing about the new page (ACCESSIBILITY.md § Route change).
 *
 * The initial page load is left alone: the browser already announces it, and
 * stealing focus there would skip the skip link.
 *
 * Mount it once, inside the router but above every route: pages each render their own
 * AppLayout, so an announcer inside the layout remounts on every navigation and never
 * sees a "previous" path. Pages whose template already owns arrival (`usePageArrival`
 * sets `data-page-arrival="template"` on the h1 via PageLayout) are skipped, so the
 * heading is focused and announced once, not twice.
 *
 * @usage
 * ```tsx
 * // In App, inside <BrowserRouter>, once:
 * <RouteAnnouncer />
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MAIN_ID = 'main-content';
const TEMPLATE_OWNED = '[data-page-arrival="template"]';
const ROUTE_FOCUS_ROOT = '[data-route-focus-root]';
const RESTORES_SCROLL = '[data-route-scroll-restoration="true"]';
/** Suspense and committed data placeholders carry these until a route is ready. */
const NOT_READY = '[data-route-loading], [data-route-pending="true"]';
/** Longest we observe a lazy route before giving up silently. */
const READY_TIMEOUT_MS = 10_000;
const SETTLE_MS = 100;

/**
 * Templates set document.title to "{Page} · {Module} · Forge"; the page name is the first
 * segment. Only that full three-part shape says anything about the destination: index.html's
 * default, a page that never set one, and "{Page} · {Module} - Forge" (useDocumentTitle) all
 * fall back to the heading.
 */
const STRUCTURED_TITLE = /^([^·]+?)\s·\s[^·]+?\s·\sForge$/;

function pageNameFromTitle(title: string): string {
  return STRUCTURED_TITLE.exec(title.trim())?.[1]?.trim() ?? '';
}

function isRendered(element: HTMLElement, includeClipped = false): boolean {
  if (element.closest('[aria-hidden="true"], [hidden], [inert]')) return false;
  const style = window.getComputedStyle(element);
  // `sr-only` content remains rendered for assistive technology, but focusing its clipped 1px
  // box strands a sighted keyboard user. The computed checks also cover equivalent utilities.
  const clip = style.clip.replace(/,/g, ' ');
  const isClipped = element.classList.contains('sr-only')
    || style.clipPath === 'inset(50%)'
    || /rect\(0(?:px)?\s+0(?:px)?\s+0(?:px)?\s+0(?:px)?\)/.test(clip);
  return style.display !== 'none' && style.visibility !== 'hidden' && (includeClipped || !isClipped);
}

function headingText(heading: HTMLElement): string {
  return heading.textContent?.trim() ?? '';
}

function pageHeadings(root: ParentNode, includeClipped = false): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('h1')).filter(
    (heading) => !heading.closest('[data-route-shell-heading]') && isRendered(heading, includeClipped),
  );
}

function snapshotHeadings(): Map<Element, string> {
  return new Map(pageHeadings(document, true).map((heading) => [heading, headingText(heading)]));
}

function findDestinationHeading(
  root: ParentNode,
  departureHeadings: Map<Element, string>,
  includeClipped = false,
): HTMLElement | null {
  const headings = pageHeadings(root, includeClipped);
  const destinationHeading = headings.find(
    (heading) => departureHeadings.get(heading) !== headingText(heading),
  );
  // A route with one stable heading still has an unambiguous page title (and keeps the
  // announcer useful for route components that update without replacing their heading).
  return destinationHeading ?? (headings.length === 1 ? headings[0] : null);
}

function findFragmentTarget(hash: string): HTMLElement | null {
  if (!hash) return null;
  const encodedId = hash.slice(1);
  let id = encodedId;
  try {
    id = decodeURIComponent(encodedId);
  } catch {
    // A malformed escape cannot match a decoded id; try the literal fragment instead.
  }
  const target = document.getElementById(id);
  return target && isRendered(target) ? target : null;
}

function destinationOwnsFocus(root: HTMLElement, departureFocus: Element | null): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body || active === document.documentElement) {
    return false;
  }
  // A persistent shell control (notably MemberShell's nav links inside #main-content) can stay
  // focused across the commit. It belongs to the page we left, not the destination.
  if (active === departureFocus) return false;
  // The fallback is a focus anchor, not a container. On a headingless standalone route, any
  // other focused element was claimed by the destination (for example an autoFocus input).
  return root.matches(ROUTE_FOCUS_ROOT) ? !active.matches(ROUTE_FOCUS_ROOT) : root.contains(active);
}

export function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const previousPathname = useRef<string | null>(null);
  // Capture the page being left during render, before the route commit replaces its descendants.
  // Search-only replacements do not update this snapshot or cancel a pending pathname arrival.
  const pathnameDeparture = useRef({
    pathname: location.pathname,
    focus: null as Element | null,
    title: document.title,
    headings: new Map<Element, string>(),
  });
  if (pathnameDeparture.current.pathname !== location.pathname) {
    // Render precedes the route commit, so this still points at the control on the page being
    // left and the headings already on screen. New or changed nodes belong to the destination.
    pathnameDeparture.current = {
      pathname: location.pathname,
      focus: document.activeElement,
      title: document.title,
      headings: snapshotHeadings(),
    };
  }
  // Read when the page arrives; a hash-only change is not a page change, so it is not a dependency.
  const hashRef = useRef(location.hash);
  hashRef.current = location.hash;

  useEffect(() => {
    const previous = previousPathname.current;
    const departureFocus = pathnameDeparture.current.focus;
    const departureHeadings = pathnameDeparture.current.headings;
    const departureTitle = pathnameDeparture.current.title;
    previousPathname.current = location.pathname;
    // First render, or a search/hash-only change: not a page change.
    if (previous === null || previous === location.pathname) return;

    // Cleared first so an identical message on the next page still mutates the live region.
    setAnnouncement('');

    let settleId: ReturnType<typeof setTimeout> | undefined;
    let done = false;
    const observer = new MutationObserver(() => settle());

    const arrive = () => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(capId);
      // The persistent shell is one scroll container; standalone pages (sign-in, reset) have
      // no AppLayout, so fall back to any <main>, then to the announcer's focus root.
      const shell = document.getElementById(MAIN_ID);
      const root = shell
        ?? document.querySelector<HTMLElement>('main')
        ?? document.querySelector<HTMLElement>(ROUTE_FOCUS_ROOT)
        ?? document.body;
      // The template already focuses and announces this page.
      if (root.querySelector(TEMPLATE_OWNED)) return;
      // Start at the top unless a destination fragment or an explicitly mounted restoration
      // owner controls scroll. Restoration may belong to POP or to a state-driven PUSH return.
      const restoresScroll = shell?.matches(RESTORES_SCROLL);
      if (shell && !hashRef.current && !restoresScroll) shell.scrollTop = 0;
      // App routes keep their heading inside the shared shell. Standalone layouts can place the
      // page heading in a sibling header, so search their whole document. Within nested shells,
      // prefer a heading that is new or changed since departure over a persistent shell title.
      const headingScope = shell ? root : document;
      const heading = findDestinationHeading(headingScope, departureHeadings);
      // PageLayout's hideTitle still supplies an accessible page name. Use it only when
      // there is no visible heading, without moving keyboard focus into its clipped box.
      const namingHeading = heading ?? findDestinationHeading(headingScope, departureHeadings, true);
      const fragmentTarget = findFragmentTarget(hashRef.current);
      const target = fragmentTarget ?? heading ?? (root === document.body ? null : root);
      if (target && !destinationOwnsFocus(root, departureFocus)) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
      // Prefer destination semantics over document.title: standalone routes do not all update
      // the title, so a structured title can still belong to the page we just left.
      const name = namingHeading?.textContent?.trim()
        || (!root.matches(ROUTE_FOCUS_ROOT) ? root.getAttribute('aria-label')?.trim() : '')
        || (document.title !== departureTitle ? pageNameFromTitle(document.title) : '')
        || '';
      setAnnouncement(name ? `${name} loaded` : 'Page loaded');
    };

    // The destination is ready once the lazy-route fallback is gone. Resolving a lazy
    // component does not change the pathname, so watch the DOM instead of timing once.
    function settle() {
      clearTimeout(settleId);
      settleId = setTimeout(() => {
        if (!document.querySelector(NOT_READY)) arrive();
      }, SETTLE_MS);
    }
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-route-loading', 'data-route-pending'],
      childList: true,
      subtree: true,
    });
    const capId = setTimeout(() => {
      done = true;
      observer.disconnect();
      clearTimeout(settleId);
    }, READY_TIMEOUT_MS);
    settle();

    return () => {
      done = true;
      observer.disconnect();
      clearTimeout(settleId);
      clearTimeout(capId);
    };
  }, [location.pathname]);

  return (
    <>
      <div data-route-focus-root tabIndex={-1} aria-label="Page content" className="sr-only" />
      <div aria-live="polite" aria-atomic="true" role="status" className="sr-only">
        {announcement}
      </div>
    </>
  );
}
